import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Report() {
  const location = useLocation();
  const navigate = useNavigate();
  const trackId = new URLSearchParams(location.search).get("trackId");
  const userId = new URLSearchParams(location.search).get("userId");
  const [currentUser, setCurrentUser] = useState(null);
  const [reason, setReason] = useState("");
  const [reportType, setReportType] = useState("");

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { session } } = await digitalOcean.auth.getSession();
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await digitalOcean
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);
  };

  const reportMutation = useMutation({
    mutationFn: async () => {
      const { error } = await digitalOcean
        .from("reports")
        .insert({
          reporter_id: currentUser.id,
          reported_track_id: trackId || null,
          reported_user_id: userId || null,
          reason: `${reportType}: ${reason}`,
          status: "pending",
        });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Report submitted successfully");
      navigate(-1);
    },
    onError: () => {
      toast.error("Failed to submit report");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reportType || !reason.trim()) {
      toast.error("Please select a reason and provide details");
      return;
    }
    reportMutation.mutate();
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" tooltip="Cancel and go back">
          ← Back
        </Button>

        <div className="bg-card p-8 rounded-lg border border-border">
          <div className="flex items-center gap-3 mb-6">
            <Flag className="w-8 h-8 text-destructive" />
            <h1 className="text-3xl font-bold text-card-foreground">Submit Report</h1>
          </div>

          <p className="text-muted-foreground mb-6">
            Help keep our community safe by reporting inappropriate content or behavior.
            All reports are reviewed by our moderation team.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Report Type
              </label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spam">Spam</SelectItem>
                  <SelectItem value="harassment">Harassment</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="copyright">Copyright Violation</SelectItem>
                  <SelectItem value="impersonation">Impersonation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Details
              </label>
              <Textarea
                placeholder="Please provide specific details about why you're reporting this content..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={6}
                required
              />
            </div>

            <div className="bg-muted/50 p-4 rounded">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> False reports may result in action against your account.
                Reports are anonymous and will be reviewed within 24-48 hours.
              </p>
            </div>

            <div className="flex gap-4">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={reportMutation.isPending}
                tooltip={reportMutation.isPending ? "Submitting your report..." : "Submit this report for moderation review"}
              >
                {reportMutation.isPending ? "Submitting..." : "Submit Report"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate(-1)}
                tooltip="Cancel and go back without submitting"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
